import * as React from "react";

import { SessionIngestedTriggers } from "@/shared/components/sessions";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type SessionRecord } from "@/shared/domain/sessions";
import {
  useCompleteSessions,
  useIsSessionComplete,
  useSessionIngestionStatus,
} from "@/shared/domain/sessions/hooks";
import { CircleCheck } from "@/shared/foundation/icons";
import {
  Banner,
  ConfirmDialog,
  Dialog,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./CompleteSessionDialog.module.css";

/**
 * Marks an in-progress session complete, after asking. Nothing for a complete
 * session, including one Roboto completed on its own since the record was
 * fetched, or in orgs without session ingestion.
 */
export function MarkSessionCompleteButton({
  session,
}: {
  session: SessionRecord;
}) {
  const released = useCheckFeatureFlag(
    "release-session-ingestion",
    session.org_id,
  );
  const [confirming, setConfirming] = React.useState(false);
  const status = useSessionIngestionStatus({
    session,
    enabled:
      released &&
      session.status === "in_progress" &&
      session.completion_policy !== null,
  });
  const complete = useIsSessionComplete(session, status);
  if (!released || complete) {
    return null;
  }
  return (
    <>
      <RobotoButton
        variant="secondary"
        size="sm"
        icon={<CircleCheck aria-hidden="true" />}
        onClick={() => setConfirming(true)}
        data-testid="mark-session-complete-button"
      >
        Mark complete
      </RobotoButton>
      <CompleteSessionDialog
        sessions={[session]}
        open={confirming}
        onOpenChange={setConfirming}
      />
    </>
  );
}

export interface CompleteSessionDialogProps {
  /**
   * The sessions to mark complete, all from one org. Complete ones are left
   * as they are, and the dialog says so.
   */
  sessions: SessionRecord[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after every in-progress session was marked complete. */
  onCompleted?: () => void;
}

/**
 * Asks before marking sessions complete, naming the triggers that start
 * after they are fully ingested, then marks them. A session that fails stays
 * listed with its error, and confirming again retries only the failed ones.
 */
export function CompleteSessionDialog({
  sessions,
  open,
  onOpenChange,
  onCompleted,
}: CompleteSessionDialogProps) {
  const { mutate, isPending, data, reset } = useCompleteSessions();
  const inProgress = sessions.filter((s) => s.status === "in_progress");
  const failedIds = new Set(data?.failed.map((f) => f.sessionId));
  const remaining = data
    ? inProgress.filter((s) => failedIds.has(s.session_id))
    : inProgress;

  const close = () => {
    reset();
    onOpenChange(false);
  };

  if (inProgress.length === 0) {
    return (
      <Dialog
        role="alertdialog"
        open={open}
        onOpenChange={(next) => !next && close()}
      >
        <Dialog.Content>
          <Dialog.Title>Nothing to mark complete</Dialog.Title>
          <Dialog.Description>
            {sessions.length === 1
              ? "This session is already complete."
              : "Every selected session is already complete."}
          </Dialog.Description>
          <div className={styles.actions}>
            <Dialog.Close
              render={<RobotoButton variant="secondary">Close</RobotoButton>}
            />
          </div>
        </Dialog.Content>
      </Dialog>
    );
  }

  const single = inProgress.length === 1;
  const alreadyComplete = sessions.length - inProgress.length;
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        } else {
          onOpenChange(next);
        }
      }}
      title={
        single
          ? "Mark this session complete?"
          : `Mark ${remaining.length} ${remaining.length === 1 ? "session" : "sessions"} complete?`
      }
      description={
        single
          ? "This tells Roboto no more files are coming."
          : "This tells Roboto no more files are coming to these sessions."
      }
      confirmLabel="Mark complete"
      pending={isPending}
      onConfirm={() =>
        mutate(
          { sessionIds: remaining.map((s) => s.session_id) },
          {
            onSuccess: (result) => {
              if (result.failed.length === 0) {
                close();
                onCompleted?.();
              }
            },
          },
        )
      }
    >
      <TriggersThatStart orgId={inProgress[0].org_id} single={single} />
      <p className={styles.note}>
        {single
          ? "Adding a file later puts the session back in progress."
          : "Adding a file to one of them later puts it back in progress."}
      </p>
      {alreadyComplete > 0 && (
        <p className={styles.note}>
          {alreadyComplete === 1
            ? "1 selected session is already complete and stays as it is."
            : `${alreadyComplete} selected sessions are already complete and stay as they are.`}
        </p>
      )}
      {data && data.failed.length > 0 && (
        <CompletionFailures
          failed={data.failed}
          completedCount={data.completed.length}
          sessions={inProgress}
        />
      )}
    </ConfirmDialog>
  );
}

/** The triggers that start when the sessions are fully ingested. */
function TriggersThatStart({
  orgId,
  single,
}: {
  orgId: string;
  single: boolean;
}) {
  return (
    <SessionIngestedTriggers
      orgId={orgId}
      lead={
        single
          ? "When all files that match an ingestion rule are ingested, the session is fully ingested"
          : "When all files that match an ingestion rule are ingested, each session is fully ingested"
      }
      installsMetrics={
        single
          ? "when you mark this session complete"
          : "when you mark these sessions complete"
      }
    />
  );
}

function CompletionFailures({
  failed,
  completedCount,
  sessions,
}: {
  failed: { sessionId: string; error: Error }[];
  completedCount: number;
  sessions: SessionRecord[];
}) {
  if (sessions.length === 1) {
    return (
      <Banner variant="error">
        {userFacingErrorMessage(
          failed[0].error,
          "Failed to mark the session complete.",
        )}
      </Banner>
    );
  }
  const names = new Map(sessions.map((s) => [s.session_id, s.name]));
  return (
    <Banner
      variant="error"
      title={`Roboto could not mark ${failed.length} of these sessions complete.`}
      description={
        <>
          <ul className={styles.failures}>
            {failed.map(({ sessionId, error }) => (
              <li key={sessionId}>
                {names.get(sessionId) ?? sessionId}:{" "}
                {userFacingErrorMessage(error, "Something went wrong.")}
              </li>
            ))}
          </ul>
          <p>
            {completedCount === 1 && "The other one is now complete. "}
            {completedCount > 1 &&
              `The other ${completedCount} are now complete. `}
            Select Mark complete to try the failed ones again.
          </p>
        </>
      }
    />
  );
}
